import { InfoIcon, MailIcon, PhoneIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { formatting } from "@/lib/formatting";
import { misc } from "@/lib/misc";
import {
  DuplicateField,
  ExistingOrPendingUserWithDetails,
  PreservedFormData,
} from "@/types";

const duplicateFieldToLabel: Record<DuplicateField, string> = {
  [DuplicateField.PHONE]: "phone number",
  [DuplicateField.EMAIL]: "email",
  [DuplicateField.BOTH]: "phone number and email",
  [DuplicateField.UNKNOWN]: "contact info",
};

export const DuplicateUserDialog = ({
  existingUser,
  isLoading,
  onAddToPipeline,
  onOpenChange,
  open,
  submittedFormData,
}: {
  existingUser: ExistingOrPendingUserWithDetails;
  isLoading: boolean;
  onAddToPipeline: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  submittedFormData: PreservedFormData;
}) => {
  /* Helpers */
  const submittedPhone = submittedFormData.createUserRequest.phone;
  const submittedEmail = submittedFormData.createUserRequest.email;

  const phoneMatches =
    !!submittedPhone &&
    !formatting.stringsDifferIgnoreCase(submittedPhone, existingUser.phone);
  const emailMatches =
    !!submittedEmail &&
    !formatting.stringsDifferIgnoreCase(submittedEmail, existingUser.email);

  const duplicateField =
    phoneMatches && emailMatches
      ? DuplicateField.BOTH
      : phoneMatches
        ? DuplicateField.PHONE
        : emailMatches
          ? DuplicateField.EMAIL
          : DuplicateField.UNKNOWN;

  const fullName = formatting.fullName(
    existingUser.firstName,
    existingUser.lastName,
  );

  // Fields the staffer entered that differ from what's on file can only be
  // changed via #staffer-feedback — spell out exactly what they entered.
  const phoneMismatch =
    !!submittedPhone &&
    formatting.stringsDifferIgnoreCase(submittedPhone, existingUser.phone);
  const emailMismatch =
    !!submittedEmail &&
    formatting.stringsDifferIgnoreCase(submittedEmail, existingUser.email);

  const changeRequestNote = (() => {
    const enteredPhone = misc.formatPhoneNumberOnCopy(submittedPhone);
    if (phoneMismatch && emailMismatch) {
      return `If you need to change the phone number to ${enteredPhone} or the email to ${submittedEmail} like you entered, please request this in #staffer-feedback.`;
    }
    if (phoneMismatch) {
      return `If you need to change the phone number to ${enteredPhone} like you entered, please request this in #staffer-feedback.`;
    }
    if (emailMismatch) {
      return `If you need to change the email to ${submittedEmail} like you entered, please request this in #staffer-feedback.`;
    }
    return null;
  })();

  /* Render */
  const render = () => (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            User already exists
          </DialogTitle>
          <DialogDescription className="!m-0 text-base font-medium text-slate-700">
            A user with this {duplicateFieldToLabel[duplicateField]} was found.
            You can add them directly to your pipeline.
          </DialogDescription>
        </DialogHeader>

        <Column className="gap-3 rounded-lg border border-slate-200 p-4">
          <Text variant="header">{fullName}</Text>
          <Column className="gap-2">
            {existingUser.phone && (
              <Row className="items-center gap-2">
                <PhoneIcon className="h-4 w-4 text-slate-400" />
                <Text variant="small">{existingUser.phone}</Text>
                {phoneMatches && (
                  <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50">
                    Match
                  </Badge>
                )}
              </Row>
            )}
            {existingUser.email && (
              <Row className="items-center gap-2">
                <MailIcon className="h-4 w-4 text-slate-400" />
                <Text variant="small">{existingUser.email}</Text>
                {emailMatches && (
                  <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50">
                    Match
                  </Badge>
                )}
              </Row>
            )}
          </Column>
          {changeRequestNote && (
            <Row className="items-start gap-1.5">
              <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
              <Text className="text-xs text-slate-400">
                {changeRequestNote}
              </Text>
            </Row>
          )}
        </Column>

        <DialogFooter>
          <Button
            className="rounded-full"
            onClick={() => {
              onOpenChange(false);
            }}
            variant="secondary"
          >
            Dismiss
          </Button>
          <Button
            className="rounded-full"
            disabled={isLoading}
            onClick={onAddToPipeline}
          >
            Add to Pipeline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return render();
};
